import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { EmployeeFormValues } from '@/types';
import { TXCenter } from '@/api/apis/facilities';
import { OpoOrganization } from '@/api/apis/opo';
import { getLocationAddress } from '@/utils/helpers';
import { useCountriesData } from '@/contexts/CountriesDataProvider';

type UseAffiliationSelectorParams = {
  txCenter?: TXCenter;
  opo?: OpoOrganization;
  onTxCenterSelect?: (value?: TXCenter) => void;
  onOpoSelect?: (value?: OpoOrganization) => void;
  onAffiliationTypeChange?: (value: string) => void;
};

export const useAffiliationSelector = ({
  txCenter,
  opo,
  onTxCenterSelect,
  onOpoSelect,
  onAffiliationTypeChange,
}: UseAffiliationSelectorParams) => {
  const { setValue } = useFormContext<EmployeeFormValues>();
  const { resolveSubdivisionName } = useCountriesData();

  const [affiliationTxCenterCode, setAffiliationTxCenterCode] = useState<string>(txCenter?.code || '');
  const [affiliationTxCenterAddress, setAffiliationTxCenterAddress] = useState<string>(
    txCenter?.location.addressLine || '',
  );
  const [affiliationOpoCode, setAffiliationOpoCode] = useState<string>(opo?.code || '');
  const [affiliationOpoAddress, setAffiliationOpoAddress] = useState<string>(opo?.location.addressLine || '');
  const [isAffiliationChanged, setIsAffiliationChanged] = useState<boolean>(false);

  const handleTxCenterSelect = (value?: TXCenter) => {
    if (value) {
      setAffiliationTxCenterAddress(getLocationAddress(value.location, resolveSubdivisionName));
      setAffiliationTxCenterCode(value.code || '');
    } else {
      setAffiliationTxCenterAddress('');
      setAffiliationTxCenterCode('');
      setValue('affiliationTxCenterId', null);
    }
    onTxCenterSelect?.(value);
  };

  const handleOpoSelect = (value?: OpoOrganization) => {
    if (value) {
      setAffiliationOpoAddress(getLocationAddress(value.location, resolveSubdivisionName));
      setAffiliationOpoCode(value.code || '');
    } else {
      setAffiliationOpoAddress('');
      setAffiliationOpoCode('');
      setValue('affiliationOpoId', null);
    }
    onOpoSelect?.(value);
  };

  const handleAffiliationTypeChange = (value: string, onChange: (value: string) => void) => {
    onChange(value);
    setAffiliationOpoAddress('');
    setAffiliationOpoCode('');
    setAffiliationTxCenterAddress('');
    setAffiliationTxCenterCode('');
    setIsAffiliationChanged(true);
    setValue('affiliationOpoId', null);
    setValue('affiliationTxCenterId', null);
    onAffiliationTypeChange?.(value);
  };

  return {
    affiliationTxCenterCode,
    affiliationTxCenterAddress,
    affiliationOpoCode,
    affiliationOpoAddress,
    isAffiliationChanged,
    handleTxCenterSelect,
    handleOpoSelect,
    handleAffiliationTypeChange,
  };
};
